import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import CaretIcon from '../../atoms/icons/caret';
import { getSiteConfig } from '../../../../siteConfig';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';

export const SectionWrapper = ({
  blok,
  destination,
  season,
  hotel,
  price,
  priceType,
  width,
  parentWidth,
}) => {
  const onlySectionsAsChildren =
    blok.blocks.every((block) => block.component === 'section') &&
    blok.blocks.length > 0;

  // Define grid column classes
  const applyColumns = (
    scrollActive,
    elemAmount,
    hasOnlySectionsAsChildren = false,
  ) => {
    if (!scrollActive) {
      return 'grid-cols-12 max-w-screen-xl';
    }

    if (elemAmount > 3) {
      return 'grid-flow-col auto-cols-[3vw] sm:auto-cols-[5vw] xl:auto-cols-[6rem] scroll-hide overflow-x-auto no-scrollbar max-w-screen-xl ';
    }

    if (elemAmount === 3) {
      if (hasOnlySectionsAsChildren) {
        return 'grid-flow-col auto-cols-[3vw] md:auto-cols-[5vw] lg:auto-cols-fr scroll-hide overflow-x-auto no-scrollbar md:max-w-screen-xl ';
      } else {
        return 'grid-flow-col auto-cols-[3vw] sm:auto-cols-[5vw] xl:auto-cols-[6rem] scroll-hide overflow-x-auto no-scrollbar max-w-screen-xl ';
      }
    }

    return 'grid-cols-12 max-w-screen-xl';
  };

  // Define grid row classes. Used for the last block against the bottom border
  const rowTemplates = {
    1: 'grid-rows-[auto]',
    2: 'grid-rows-[auto,1fr]',
    3: 'grid-rows-[auto,auto,1fr]',
    4: 'grid-rows-[auto,auto,auto,1fr]',
    5: 'grid-rows-[auto,auto,auto,auto,1fr]',
  };

  // Additional props for nested components --- --- --->
  const additionalProps = {
    width: width,
    hasHorizontalScroll: blok.horizontal_scroll || false,
  };

  const hideOnSite = (hiddenLanguages) => {
    const storyblokLanguage = getSiteConfig().storyblokLanguage;

    if (hiddenLanguages) {
      return hiddenLanguages.includes(storyblokLanguage);
    } else return false;
  };

  if (blok.horizontal_scroll) {
    return (
      <Swiper
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
          el: '.swiper-pagination',
        }}
        autoplay={
          blok.automaticSlide
            ? {
                delay: 4000,
                disableOnInteraction: true,
              }
            : false
        }
        loop={false}
        slidesPerView={
          blok.slider_max_cards > 0 ? blok.slider_max_cards : 'auto'
        }
        breakpoints={{
          320: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          // when window width is >= 480px
          768: {
            slidesPerView: 2.3,
            spaceBetween: 20,
          },
          // when window width is >= 640px
          1024: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
          1400: {
            slidesPerView:
              blok.slider_max_cards > 0
                ? blok.slider_max_cards
                : blok.blocks.length > 3
                ? 4
                : 3,
            spaceBetween: 20,
          },
        }}
        spaceBetween={20}
        modules={[Navigation, Autoplay, Pagination]}
        key={blok._uid}
        breakpointsBase="container"
        lazy="true"
        lazyPreloadPrevNext={2}
        setWrapperSize={true}
        {...storyblokEditable(blok)}
        className="w-full col-span-12 grid "
      >
        {blok.blocks.map((nestedBlok, index) => {
          if (!hideOnSite(nestedBlok.hide_on_site)) {
            return (
              <SwiperSlide key={nestedBlok._uid} style={{ display: 'flex' }}>
                <StoryblokComponent
                  blok={nestedBlok}
                  hotel={hotel}
                  price={price}
                  priceType={priceType}
                  season={season}
                  destination={destination}
                  isInSection={true}
                  hasSiblings={blok.blocks.length > 1}
                  isLast={index === blok.blocks.length - 1 ? true : false}
                  inSlider={true}
                  {...additionalProps}
                />
              </SwiperSlide>
            );
          }
        })}

        <div className="flex justify-center mt-4">
          <div className="swiper-button-prev">
            <button
              className="z-50 rotate-90 rounded-full w-7 h-7 bg-yellow-60 hover:bg-yellow md:w-8 md:h-8 flex justify-center items-center"
              aria-label="Previous"
            >
              <CaretIcon fill="#222222" />
            </button>
          </div>
          <div className="relative w-20 mb-1">
            <div className="swiper-pagination"></div>
          </div>

          <div className="swiper-button-next">
            <button
              className="z-50 -rotate-90 rounded-full w-7 h-7 bg-yellow-60 hover:bg-yellow md:w-8 md:h-8 flex justify-center items-center"
              aria-label="Next"
            >
              <CaretIcon fill="#222222" />
            </button>
          </div>
        </div>
      </Swiper>
    );
  } else {
    return (
      <div
        className={`${
          // Check if the last block should align against the bottom border
          blok.last_block_against_bottom_border
            ? `h-full items-end ${rowTemplates[blok.blocks.length]}`
            : 'h-auto'
        } w-full grid ${applyColumns(
          blok.horizontal_scroll,
          blok.blocks.length,
          onlySectionsAsChildren,
        )} ${blok.suppress_vertical_gap ? 'gap-x-4' : 'gap-4'} mx-auto`}
        id={blok._uid}
      >
        {blok.blocks.map((nestedBlok, index) => (
          <StoryblokComponent
            blok={nestedBlok}
            key={nestedBlok._uid}
            hotel={hotel}
            price={price}
            priceType={priceType}
            season={season}
            destination={destination}
            isInSection={true}
            hasSiblings={blok.blocks.length > 1}
            index={index}
            isLast={index === blok.blocks.length - 1 ? true : false}
            parentWidth={parentWidth}
            {...additionalProps}
          />
        ))}
      </div>
    );
  }
};
